import React, { useEffect, useMemo, useState } from 'react';
import { catalogApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import {
  PRODUCT_STATUS_LABEL, PRODUCT_TYPE_LABEL,
  PUBLICATION_STATUS_LABEL, REQUEST_STATUS_LABEL,
  TRANSACTION_STATUS_LABEL, DECISION_LABEL, t,
} from '../lib/catalogI18n';

// -------- Formato de fecha ES / Europe/Madrid --------
const DATE_FMT = new Intl.DateTimeFormat('es-ES', {
  timeZone: 'Europe/Madrid',
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
});
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return DATE_FMT.format(new Date(iso)).replace(',', '');
  } catch { return iso; }
}

const STATUS_STYLE = {
  published: 'bg-emerald-100 text-emerald-800',
  draft: 'bg-amber-100 text-amber-800',
  withdrawn: 'bg-gray-200 text-gray-700',
};
const PUB_STYLE = {
  published: 'bg-emerald-100 text-emerald-800',
  withdrawn: 'bg-gray-200 text-gray-700',
  rejected: 'bg-rose-100 text-rose-800',
};
const REQ_STYLE = {
  granted: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-amber-100 text-amber-800',
  denied: 'bg-rose-100 text-rose-800',
  revoked: 'bg-gray-200 text-gray-700',
};
const TX_STYLE = {
  completed: 'bg-emerald-100 text-emerald-800',
  accessed: 'bg-blue-100 text-blue-800',
  denied: 'bg-rose-100 text-rose-800',
};

export default function AdminCatalog() {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode, product}
  const [busyProductId, setBusyProductId] = useState(null);

  // Índices auxiliares para resolver referencias sin exponer UUIDs
  const productById = useMemo(() => Object.fromEntries(
    products.map(p => [p.id, p])), [products]);

  const reload = async () => {
    try {
      const [rp, rpub, rreq, rtx] = await Promise.all([
        catalogApi.list(), catalogApi.adminPublications(),
        catalogApi.adminRequests(), catalogApi.adminTransactions(),
      ]);
      setProducts(rp.data);
      setPubs(rpub.data);
      setReqs(rreq.data);
      setTxs(rtx.data);
    } catch {
      toast.error('No se pudo cargar la información del catálogo');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const openConfirm = (mode, product) => setDialog({ mode, product });
  const closeConfirm = () => { if (!busyProductId) setDialog(null); };

  const runAction = async () => {
    if (!dialog || busyProductId) return;
    const { mode, product } = dialog;
    setBusyProductId(product.id);
    const previous = product.status;
    // Actualización OPTIMISTA sólo se aplica DESPUÉS de éxito.
    try {
      const call = mode === 'withdraw'
        ? catalogApi.withdraw(product.id)
        : catalogApi.publish(product.id);
      const r = await call;
      const newStatus = r.data?.status || (mode === 'withdraw' ? 'withdrawn' : 'published');
      if (r.data?.changed === false && newStatus === previous) {
        toast.info('El estado ya estaba actualizado.');
      } else {
        toast.success(mode === 'withdraw'
          ? 'Producto retirado correctamente.'
          : 'Producto publicado correctamente.');
      }
      // Refresco desde backend: nunca mostrar un estado falso local.
      await reload();
      setDialog(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || 'La operación no pudo completarse. Vuelva a intentarlo.');
      // NO tocamos el estado visual: mantenemos el anterior.
    } finally {
      setBusyProductId(null);
    }
  };

  const tabs = [
    { id: 'products', label: 'Productos' },
    { id: 'publications', label: 'Publicaciones' },
    { id: 'requests', label: 'Solicitudes' },
    { id: 'transactions', label: 'Transacciones' },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="admin-catalog-page">
      <h1 className="text-3xl font-bold">Administración del catálogo</h1>
      <div className="flex gap-2 border-b">
        {tabs.map(x => (
          <button key={x.id} onClick={() => setTab(x.id)}
            className={`px-4 py-2 text-sm font-medium ${
              tab === x.id ? 'border-b-2 border-slate-800 text-slate-900'
                           : 'text-gray-500 hover:text-slate-700'}`}
            data-testid={`admin-tab-${x.id}`}>
            {x.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : (<>
        {tab === 'products' && (
          <ProductsTable
            products={products}
            busyId={busyProductId}
            onWithdraw={p => openConfirm('withdraw', p)}
            onPublish={p => openConfirm('publish', p)}
          />
        )}
        {tab === 'publications' && (
          <PublicationsTable rows={pubs} productById={productById} />
        )}
        {tab === 'requests' && (
          <RequestsTable rows={reqs} productById={productById} />
        )}
        {tab === 'transactions' && (
          <TransactionsTable rows={txs} productById={productById} />
        )}
      </>)}

      <ConfirmDialog
        dialog={dialog}
        busy={!!busyProductId}
        onCancel={closeConfirm}
        onConfirm={runAction}
      />
    </div>
  );
}

// ------------------- Tablas -------------------
function ProductsTable({ products, busyId, onWithdraw, onPublish }) {
  if (products.length === 0) return <p className="text-gray-500">Sin productos.</p>;
  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">Código</th>
            <th className="px-3 py-2 text-left">Título</th>
            <th className="px-3 py-2 text-left">Tipo</th>
            <th className="px-3 py-2 text-left">Estado</th>
            <th className="px-3 py-2 text-left">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {products.map(p => {
            const isBusy = busyId === p.id;
            return (
              <tr key={p.id} className="border-t" data-testid={`admin-product-row-${p.code}`}>
                <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                <td className="px-3 py-2">{p.title}</td>
                <td className="px-3 py-2">{t(PRODUCT_TYPE_LABEL, p.product_type)}</td>
                <td className="px-3 py-2">
                  <Badge className={STATUS_STYLE[p.status] || ''}>
                    {t(PRODUCT_STATUS_LABEL, p.status)}
                  </Badge>
                </td>
                <td className="px-3 py-2 space-x-2">
                  {p.status === 'draft' && (
                    <Button size="sm" disabled={isBusy}
                      data-testid={`btn-publish-${p.code}`}
                      onClick={() => onPublish(p)}>
                      Publicar producto
                    </Button>
                  )}
                  {p.status === 'published' && (
                    <Button size="sm" variant="outline" disabled={isBusy}
                      data-testid={`btn-withdraw-product-${p.code}`}
                      onClick={() => onWithdraw(p)}>
                      {isBusy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                      Retirar producto
                    </Button>
                  )}
                  {p.status === 'withdrawn' && (
                    <Button size="sm" disabled={isBusy}
                      data-testid={`btn-republish-${p.code}`}
                      onClick={() => onPublish(p)}>
                      {isBusy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                      Volver a publicar
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function productLabel(pid, productById) {
  const p = productById[pid];
  if (!p) return <span className="text-gray-500">—</span>;
  return (
    <span title={pid}>
      <span className="font-mono text-xs text-gray-500">{p.code}</span>
      <span className="mx-1">·</span>
      <span>{p.title}</span>
    </span>
  );
}

function PublicationsTable({ rows, productById }) {
  if (!rows.length) return <p className="text-gray-500">Sin publicaciones.</p>;
  return (
    <div className="bg-white rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">Código de publicación</th>
            <th className="px-3 py-2 text-left">Producto</th>
            <th className="px-3 py-2 text-left">Campaña</th>
            <th className="px-3 py-2 text-left">Estado</th>
            <th className="px-3 py-2 text-left">Versión de condiciones</th>
            <th className="px-3 py-2 text-left">Fecha de publicación</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t"
                data-testid={`admin-pub-row-${r.publication_code}`}>
              <td className="px-3 py-2 font-mono">{r.publication_code}</td>
              <td className="px-3 py-2">{productLabel(r.product_id, productById)}</td>
              <td className="px-3 py-2" title={r.campaign_id}>
                {r.period_label || r.campaign_name || <span className="font-mono text-xs text-gray-500">{(r.campaign_id || '').slice(0,8)}…</span>}
              </td>
              <td className="px-3 py-2">
                <Badge className={PUB_STYLE[r.status] || ''}>
                  {t(PUBLICATION_STATUS_LABEL, r.status)}
                </Badge>
              </td>
              <td className="px-3 py-2">{r.terms_version}</td>
              <td className="px-3 py-2">{fmtDate(r.published_at || r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RequestsTable({ rows, productById }) {
  if (!rows.length) return <p className="text-gray-500">Sin solicitudes.</p>;
  return (
    <div className="bg-white rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">Código de solicitud</th>
            <th className="px-3 py-2 text-left">Producto</th>
            <th className="px-3 py-2 text-left">Estado</th>
            <th className="px-3 py-2 text-left">Decisión</th>
            <th className="px-3 py-2 text-left">Versión de condiciones</th>
            <th className="px-3 py-2 text-left">Fecha de solicitud</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t"
                data-testid={`admin-req-row-${r.request_code}`}>
              <td className="px-3 py-2 font-mono">{r.request_code}</td>
              <td className="px-3 py-2">{productLabel(r.product_id, productById)}</td>
              <td className="px-3 py-2">
                <Badge className={REQ_STYLE[r.status] || ''}>
                  {t(REQUEST_STATUS_LABEL, r.status)}
                </Badge>
              </td>
              <td className="px-3 py-2">{t(DECISION_LABEL, r.decision)}</td>
              <td className="px-3 py-2">{r.terms_version}</td>
              <td className="px-3 py-2">{fmtDate(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTable({ rows, productById }) {
  if (!rows.length) return <p className="text-gray-500">Sin transacciones.</p>;
  return (
    <div className="bg-white rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left">Código de transacción</th>
            <th className="px-3 py-2 text-left">Producto</th>
            <th className="px-3 py-2 text-left">Indicador</th>
            <th className="px-3 py-2 text-left">Tamaño de la muestra</th>
            <th className="px-3 py-2 text-left">Estado</th>
            <th className="px-3 py-2 text-left">Fecha</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-t"
                data-testid={`admin-tx-row-${r.transaction_code}`}>
              <td className="px-3 py-2 font-mono">{r.transaction_code}</td>
              <td className="px-3 py-2">{productLabel(r.product_id, productById)}</td>
              <td className="px-3 py-2">{r.indicator_code}</td>
              <td className="px-3 py-2">{r.aggregation_count ?? '—'}</td>
              <td className="px-3 py-2">
                <Badge className={TX_STYLE[r.status] || ''}>
                  {t(TRANSACTION_STATUS_LABEL, r.status)}
                </Badge>
              </td>
              <td className="px-3 py-2">{fmtDate(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------- Modal de confirmación -------------------
function ConfirmDialog({ dialog, busy, onCancel, onConfirm }) {
  if (!dialog) return null;
  const isWithdraw = dialog.mode === 'withdraw';
  return (
    <Dialog open={!!dialog} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid={isWithdraw ? 'confirm-withdraw-dialog' : 'confirm-publish-dialog'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isWithdraw
              ? <><AlertTriangle className="w-5 h-5 text-amber-600" /> ¿Retirar este producto del catálogo?</>
              : <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> ¿Volver a publicar este producto?</>
            }
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-700 pt-2 leading-relaxed">
            {isWithdraw ? (
              <>
                El producto <strong>{dialog.product.code}</strong> — «{dialog.product.title}» dejará de estar disponible para nuevas publicaciones o solicitudes de acceso.
                El historial existente se conservará. Esta acción quedará registrada en la auditoría.
              </>
            ) : (
              <>
                El producto <strong>{dialog.product.code}</strong> — «{dialog.product.title}» volverá a estar disponible en el catálogo para nuevas publicaciones y solicitudes.
                Esta acción quedará registrada en la auditoría.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}
                  data-testid="confirm-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={onConfirm} disabled={busy}
                  data-testid="confirm-confirm-btn"
                  className={isWithdraw ? 'bg-amber-600 hover:bg-amber-700 text-white'
                                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'}>
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isWithdraw ? 'Confirmar retirada' : 'Confirmar publicación'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
